import { useState, useEffect, useMemo, useCallback, ReactElement } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Button,
  capitalize,
  Tooltip,
  Dialog,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Tag as NumberIcon,
} from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import useSession from 'src/authentication/hooks/use-session'
import { useCertificateData } from 'src/v3/utilities/hooks/use-certificate-data'
import { useCertificateDownloadManager } from 'src/v3/manage/hooks/use-certificate-download-manager'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { VerificationStatus } from 'src/v3/core/types'
import {
  CancelIcon,
  CloseIcon,
  CredentialIcon,
  DownloadIcon,
  EditIcon,
  PendingIcon,
  PreviewIcon,
  VerifiedIcon,
} from 'src/v3/core/icons'
import { DateRangeIcon } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ContentType,
  downloadFile,
  extensions,
} from 'src/v3/utilities/filesystem'
import LearningOverviewTab from 'src/v3/profile/v4/certificates/components/learning-overview'
import UserProfileCard from './components/user-profile-card'
import RoleManagementCard from './components/role-management-card'
import LearningSummaryCard from './components/learning-summary-card'
import CertificatesDataGrid from './components/certificates-data-grid'
import CredentialsDataGrid from './components/credentials-data-grid'
import LicensesDataGrid from './components/licenses-data-grid'
import UserActivityTimeline from './components/user-activity-timeline'
import {
  fetchLinkedServices,
  fetchSelectedUser,
  selectSelectedUser,
  selectSelectedUserStatus,
  selectActiveLinkedService,
  selectLinkedServices,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
  selectSelectedUserCertificates,
  selectSelectedUserCertificatesStatus,
  fetchSelectedUserCertificates,
  selectSelectedUserLicensesCredentials,
  selectSelectedUserLicensesCredentialsStatus,
  fetchSelectedUserLicensesCredentials,
  fetchLightweightUsersV4,
  selectNREMTVerification,
  selectNREMTVerificationStatus,
  fetchNREMTVerificationStatus,
  selectLicenseVerificationsStatus,
  selectLicenseVerifications,
  fetchLicenseVerificationStatuses,
  selectUserEMSID,
  selectUserEMSIDStatus,
  fetchUserEMSID,
  selectCredentialVerifications,
  selectCredentialVerificationsStatus,
  fetchCredentialVerificationStatuses,
  fetchNREMTLicense,
  selectNREMTLicense,
  selectNREMTLicenseStatus,
  // Activity timeline selectors and actions are now handled by the hook
} from '../manage.slice'
import { ROSTER_URLS } from '../constants'
import { useNotifications } from '../hooks/use-notifications'
import { VerifyNREMT } from './components/modals/verify-nremt'
import { NREMTLicenseForm } from './components/modals/edit-nremt-modal'

// TabPanel component for tab content
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const verificationStatusColor: Record<VerificationStatus, string> = {
  accepted: 'success',
  pending: 'warning',
  rejected: 'error',
}

const iconSX = { fontSize: 18, color: 'text.secondary' }
const verificationStatusIcon: Record<VerificationStatus, ReactElement> = {
  accepted: <VerifiedIcon aria-hidden sx={iconSX} />,
  pending: <PendingIcon aria-hidden sx={iconSX} />,
  rejected: <CancelIcon aria-hidden sx={iconSX} />,
}

function TabPanel(props: TabPanelProps) {
  let { children, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-details-tabpanel-${index}`}
      aria-labelledby={`user-details-tab-${index}`}
    >
      {value === index && <Box className="pt-6">{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `user-details-tab-${index}`,
    'aria-controls': `user-details-tabpanel-${index}`,
  }
}

export default function UserDetails() {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let session = useSession()
  let { linkedServiceID, userID } = useParams()
  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let [activeTab, setActiveTab] = useState(0)
  let [open, setOpen] = useState(false)
  let [downloading, setDownloading] = useState<'preview' | 'download'>()
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)
  let [nremtOpen, setNREMTOpen] = useState(false)

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let user = useAppSelector(selectSelectedUser)
  let emsID = useAppSelector(selectUserEMSID(userID || ''))
  let emsIDStatus = useAppSelector(selectUserEMSIDStatus(userID || ''))
  let selectedUserStatus = useAppSelector(selectSelectedUserStatus)
  let selectedUserCertificatesStatus = useAppSelector(
    selectSelectedUserCertificatesStatus,
  )
  let selectedUserCertificates = useAppSelector(selectSelectedUserCertificates)
  let selectedUserLicensesCredentialsStatus = useAppSelector(
    selectSelectedUserLicensesCredentialsStatus,
  )
  let selectedUserLicensesCredentials = useAppSelector(
    selectSelectedUserLicensesCredentials,
  )
  let nremt = useAppSelector(selectNREMTLicense(userID || ''))
  let nremtStatus = useAppSelector(selectNREMTLicenseStatus(userID || ''))
  let nremtVerification = useAppSelector(selectNREMTVerification(userID || ''))
  let nremtVerificationStatus = useAppSelector(
    selectNREMTVerificationStatus(userID || ''),
  )
  let licenseVerifications = useAppSelector(selectLicenseVerifications)
  let licenseVerificationsStatus = useAppSelector(
    selectLicenseVerificationsStatus(linkedServiceID || ''),
  )
  let credentialVerifications = useAppSelector(selectCredentialVerifications)
  let credentialVerificationsStatus = useAppSelector(
    selectCredentialVerificationsStatus(linkedServiceID || ''),
  )

  // Use manager-specific certificate download hook
  let managerDownloadHook = useCertificateDownloadManager()

  // Extract certificate data using custom hook
  let {
    certificateCounts,
    transformedCertificates,
    certificateColumns,
    CustomToolbar,
  } = useCertificateData(
    selectedUserCertificates,
    selectedUserCertificatesStatus === 'pending',
    selectedUserCertificatesStatus === 'rejected',
    managerDownloadHook,
  )

  // Process credentials data and calculate summary stats (memoized)
  let { credentialsSummary } = useMemo(() => {
    // Backend now returns id field for both credentials and licenses
    let summary = {
      total: selectedUserLicensesCredentials.length,
      licenses: selectedUserLicensesCredentials.filter(
        (item) => item.category === 'License',
      ),
      expiredLicenses: selectedUserLicensesCredentials.filter(
        (item) => item.category === 'License' && item.status === 'expired',
      ).length,
      expiringSoonLicenses: selectedUserLicensesCredentials.filter(
        (item) =>
          item.category === 'License' && item.status === 'expiring-soon',
      ).length,
      credentials: selectedUserLicensesCredentials.filter(
        (item) => item.category === 'Credential',
      ),
      expired: selectedUserLicensesCredentials.filter(
        (item) => item.status === 'expired',
      ).length,
      expiringSoon: selectedUserLicensesCredentials.filter(
        (item) => item.status === 'expiring-soon',
      ).length,
    }

    return { credentialsSummary: summary }
  }, [selectedUserLicensesCredentials])

  // Destructure summary for backwards compatibility
  let {
    licenses,
    expiredLicenses,
    expiringSoonLicenses,
    credentials,
    expired: expiredCredentials,
    expiringSoon: expiringSoonCredentials,
  } = credentialsSummary

  // Check if credential verification is enabled for active linked service
  let verificationEnabled =
    linkedServices.find((ls) => ls.id === activeLinkedService)
      ?.credentialVerification === 'full' || false
  let licenseVerificationEnabled =
    linkedServices.find((ls) => ls.id === activeLinkedService)
      ?.licenseVerification === 'full' || false
  let nremtVerificationEnabled =
    linkedServices.find((ls) => ls.id === activeLinkedService)
      ?.nremtVerification === 'full' || false

  let formattedNremtDate =
    nremt?.expiration && dayjs(nremt.expiration).isValid()
      ? dayjs(nremt.expiration).format('MMM D, YYYY')
      : null

  let formattedNremtIssueDate =
    nremt?.issueDate && dayjs(nremt.issueDate).isValid()
      ? dayjs(nremt.issueDate).format('MMM D, YYYY')
      : null

  useScrollToTop()

  // Redirect logic for missing route parameters
  useEffect(() => {
    if (!linkedServiceID || !userID) {
      navigate(ROSTER_URLS.INDEX, {
        state: { linkedServiceID },
      })
    }
  }, [linkedServiceID, userID, navigate])

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (linkedServiceID !== activeLinkedService) {
      dispatch(activeLinkedServiceChanged(linkedServiceID!))
    }
  }, [dispatch, linkedServiceID, activeLinkedService])

  useEffect(() => {
    if (userID && (!emsIDStatus || emsIDStatus === 'uninitialized')) {
      dispatch(fetchUserEMSID(userID))
    }
  }, [userID, emsIDStatus])

  useEffect(() => {
    if (user?.id !== userID) {
      dispatch(fetchSelectedUser(userID!))
    }
  }, [dispatch, userID, user?.id])

  useEffect(() => {
    if (userID && (!nremtStatus || nremtStatus === 'uninitialized')) {
      dispatch(fetchNREMTLicense(userID))
    }
  }, [dispatch, userID, nremtStatus])

  useEffect(() => {
    if (
      linkedServiceID &&
      userID &&
      (!nremtVerificationStatus || nremtVerificationStatus === 'uninitialized')
    ) {
      dispatch(fetchNREMTVerificationStatus({ linkedServiceID, userID }))
    }
  }, [dispatch, userID, linkedServiceID])

  useEffect(() => {
    if (
      linkedServiceID &&
      (!licenseVerificationsStatus ||
        licenseVerificationsStatus === 'uninitialized')
    ) {
      dispatch(fetchLicenseVerificationStatuses(linkedServiceID))
    }
  }, [dispatch, userID, linkedServiceID])

  useEffect(() => {
    if (
      linkedServiceID &&
      (!credentialVerificationsStatus ||
        credentialVerificationsStatus === 'uninitialized')
    ) {
      dispatch(fetchCredentialVerificationStatuses(linkedServiceID))
    }
  }, [dispatch, userID, linkedServiceID])

  // Key these effects on userID/linkedServiceID only. Don't gate on user?.id
  // or the fetch status — user is loaded by a separate effect, so user?.id is
  // briefly undefined on mount, and including the status here creates a
  // feedback loop as it transitions uninitialized → pending → fulfilled.
  // Both caused duplicate dispatches before the first response landed.
  useEffect(() => {
    if (userID && linkedServiceID) {
      dispatch(fetchSelectedUserCertificates({ id: userID, linkedServiceID }))
    }
  }, [dispatch, userID, linkedServiceID])

  useEffect(() => {
    if (userID) {
      dispatch(fetchSelectedUserLicensesCredentials(userID))
    }
  }, [dispatch, userID])

  let getLinkedServiceName = () => {
    if (activeLinkedService === 'unselected') return 'No Service Selected'
    let service = linkedServices.find((s) => s.id === activeLinkedService)
    return service?.name || 'Unknown Service'
  }

  let handleRoleSuccess = useCallback((message: string) => {
    showSuccess(message)
  }, [])

  let handleRoleError = useCallback((message: string) => {
    showError(message)
  }, [])

  let handleRemoveFromRoster = useCallback(() => {
    // Refresh the lightweight users list for the roster
    if (linkedServiceID) {
      dispatch(fetchLightweightUsersV4(linkedServiceID))
    }
    navigate(ROSTER_URLS.INDEX, {
      state: { linkedServiceID },
    })
  }, [navigate, dispatch, linkedServiceID])

  let handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue)
    },
    [],
  )

  // Memoized navigation handler
  let handleBackNavigation = useCallback(() => {
    navigate(ROSTER_URLS.INDEX, {
      state: { linkedServiceID },
    })
  }, [navigate, linkedServiceID])

  if (selectedUserStatus === 'pending' || (!user && userID)) {
    return (
      <Container maxWidth="xl" className="py-8">
        <Box className="mb-4">
          <Typography variant="body2" color="text.secondary">
            <BusinessIcon
              className="mr-2 align-middle"
              sx={{ fontSize: 16 }}
              aria-hidden="true"
            />
            {getLinkedServiceName()}
          </Typography>
        </Box>
        <Box className="grid grid-cols-[auto_1fr] gap-2 items-center mb-4">
          <IconButton
            onClick={handleBackNavigation}
            aria-label="Go back to roster"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">User Details</Typography>
        </Box>
        <Card className="mb-6">
          <CardContent className="p-8">
            <Box className="grid items-center grid-cols-1 gap-6 lg:grid-cols-12">
              <Box className="grid place-items-center lg:col-span-2 lg:place-items-start">
                <Skeleton variant="circular" width={120} height={120} />
              </Box>
              <Box className="lg:col-span-10">
                <Skeleton
                  variant="text"
                  width={200}
                  height={32}
                  className="mb-4"
                />
                <Box className="grid grid-cols-1 gap-2">
                  <Skeleton variant="text" width={250} height={20} />
                  <Skeleton variant="text" width={180} height={20} />
                  <Skeleton variant="text" width={300} height={20} />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Box className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
          <Box className="lg:col-span-2">
            <Card className="h-full">
              <CardContent className="p-8">
                <Skeleton
                  variant="text"
                  width={150}
                  height={28}
                  className="mb-6"
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={20}
                  className="mb-4"
                />
                <Box className="grid max-w-sm grid-cols-1 gap-2 mb-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
                  <Skeleton variant="rectangular" width={80} height={32} />
                </Box>
                <Skeleton
                  variant="text"
                  width={80}
                  height={20}
                  className="mb-4"
                />
                <Box className="grid max-w-lg grid-cols-1 gap-3 md:grid-cols-2">
                  <Skeleton variant="rectangular" width={180} height={36} />
                  <Skeleton variant="rectangular" width={180} height={36} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box className="lg:col-span-1">
            <Card className="h-full">
              <CardContent className="p-8">
                <Skeleton
                  variant="text"
                  width={130}
                  height={28}
                  className="mb-6"
                />
                <Box className="grid grid-cols-1 gap-4">
                  {[1, 2, 3].map((item) => (
                    <Box
                      key={item}
                      className="grid grid-cols-[auto_auto_1fr] gap-3 items-center"
                    >
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="text" width={40} height={32} />
                      <Skeleton variant="text" width={120} height={16} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Card className="mt-6">
          <Box
            className="px-6 pt-4"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Box className="flex gap-6">
              <Skeleton variant="text" width={180} height={32} />
              <Skeleton variant="text" width={140} height={32} />
              <Skeleton variant="text" width={120} height={32} />
            </Box>
          </Box>
          <CardContent className="p-8">
            <Skeleton variant="text" width={300} height={24} className="mb-4" />
            <Skeleton variant="text" width={450} height={16} className="mb-6" />
            <Skeleton variant="rectangular" height={500} className="rounded" />
          </CardContent>
        </Card>
      </Container>
    )
  }

  if (!user) {
    return (
      <Container maxWidth="xl" className="py-8">
        <Box className="mb-4">
          <Typography variant="body2" color="text.secondary">
            <BusinessIcon
              className="mr-2 align-middle"
              sx={{ fontSize: 16 }}
              aria-hidden="true"
            />
            {getLinkedServiceName()}
          </Typography>
        </Box>
        <Box className="grid grid-cols-[auto_1fr] gap-2 items-center mb-4">
          <IconButton
            onClick={handleBackNavigation}
            aria-label="Go back to roster"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">User Details</Typography>
        </Box>
        <Box className="grid py-8 place-items-center">
          <Typography color="error">User not found</Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-8">
      <Box className="mb-4">
        <Typography variant="body2" color="text.secondary">
          <BusinessIcon
            className="mr-2 align-middle"
            sx={{ fontSize: 16 }}
            aria-hidden="true"
          />
          {getLinkedServiceName()}
        </Typography>
      </Box>
      <Box className="grid grid-cols-[auto_1fr] gap-2 items-center mb-4">
        <IconButton
          onClick={handleBackNavigation}
          aria-label="Go back to roster"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">User Details</Typography>
      </Box>
      <UserProfileCard
        user={user}
        emsID={emsID}
        verificationEnabled={nremtVerificationEnabled}
        verificationStatus={nremtVerification}
      />
      <Box className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
        <RoleManagementCard
          user={user}
          sessionUserId={session?.userId}
          linkedServiceID={linkedServiceID!}
          onSuccess={handleRoleSuccess}
          onError={handleRoleError}
          onRemoveFromRoster={handleRemoveFromRoster}
        />

        <LearningSummaryCard
          certificateCounts={certificateCounts}
          selectedUserCertificatesStatus={selectedUserCertificatesStatus}
        />
      </Box>
      <Card className="mt-6">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="User details tabs"
          >
            <Tab label="Certificates & Learning Records" {...a11yProps(0)} />
            <Tab label="Licenses & Certifications" {...a11yProps(1)} />
            <Tab label="Credentials" {...a11yProps(2)} />
            <Tab label="Activity Timeline" {...a11yProps(3)} />
            <Tab label="Learning Overview" {...a11yProps(4)} />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <CertificatesDataGrid
            transformedCertificates={transformedCertificates}
            certificateColumns={certificateColumns}
            selectedUserCertificatesStatus={selectedUserCertificatesStatus}
            CustomToolbar={CustomToolbar}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2, mb: 1, mt: -1 }}>
            <Card
              variant="outlined"
              sx={{ px: 2, py: 1.5, display: 'inline-block' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <CredentialIcon color="primary" sx={{ mb: 0.25 }} />
                <Typography sx={{ fontSize: 20 }}>NREMT</Typography>
                {nremt && (
                  <IconButton
                    aria-label="Edit NREMT license"
                    size="small"
                    onClick={() => setNREMTOpen(true)}
                  >
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                )}
              </Box>
              {!nremt ? (
                <Box>
                  <Typography>
                    This user does not have an NREMT record.
                  </Typography>
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    onClick={() => setNREMTOpen(true)}
                  >
                    Create NREMT
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    columnGap: 3,
                    rowGap: 0.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box className="grid grid-cols-[auto_1fr] gap-1 items-center">
                    <NumberIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`NREMT Certification Number: ${nremt.nremtidNumber}`}
                    >
                      <strong>NREMT Certification Number:</strong>{' '}
                      {nremt.nremtidNumber}
                    </Typography>
                  </Box>
                  {formattedNremtIssueDate && (
                    <Box className="grid grid-cols-[auto_1fr] gap-1 items-center">
                      <DateRangeIcon
                        sx={{ fontSize: 18, color: 'text.secondary' }}
                        aria-hidden="true"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        aria-label={`NREMT Issue Date: ${formattedNremtIssueDate}`}
                      >
                        <strong>Issue Date:</strong> {formattedNremtIssueDate}
                      </Typography>
                    </Box>
                  )}
                  <Box className="grid grid-cols-[auto_1fr] gap-1 items-center">
                    <DateRangeIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`NREMT Certification Date: ${formattedNremtDate}`}
                    >
                      <strong>Expiration:</strong> {formattedNremtDate}
                    </Typography>
                  </Box>
                  {nremtVerificationEnabled && nremtVerification && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                      }}
                    >
                      {verificationStatusIcon[nremtVerification]}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        aria-label={`NREMT Verification Status: ${nremtVerification}`}
                      >
                        <strong>
                          Verification{' '}
                          <Typography
                            variant="body2"
                            component="span"
                            color={verificationStatusColor[nremtVerification]}
                            sx={{ fontWeight: 'inherit' }}
                          >
                            {capitalize(nremtVerification)}
                          </Typography>
                        </strong>
                      </Typography>
                    </Box>
                  )}
                  {nremtVerificationEnabled &&
                    nremtVerification === 'pending' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setOpen(true)}
                        >
                          Verify NREMT
                        </Button>
                        <VerifyNREMT
                          open={open}
                          setOpen={setOpen}
                          userID={user.id}
                          linkedServiceID={activeLinkedService}
                        />
                      </>
                    )}
                  {nremt.imageName && (
                    <Box>
                      <Tooltip
                        title={
                          nremt.imageName.includes('pdf')
                            ? 'Cannot preview PDF files. Download the file to view.'
                            : 'View'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Preview User NREMT"
                            loading={downloading === 'preview'}
                            onClick={() => {
                              setDownloading('preview')
                              axios
                                .get<string>(
                                  `/api/manage/nremt/image/${nremt.id}`,
                                )
                                .then(pluckData)
                                .then((data) =>
                                  axios
                                    .get<Blob>(data, { responseType: 'blob' })
                                    .then(pluckData),
                                )
                                .then((data) => setViewBlob(data))
                                .catch((err) =>
                                  showError(
                                    err?.response?.data ||
                                      err?.message ||
                                      'Error Getting License',
                                  ),
                                )
                                .finally(() => setDownloading(undefined))
                            }}
                            disabled={nremt.imageName.includes('pdf')}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          color="primary"
                          loading={downloading === 'download'}
                          aria-label="Download User NREMT"
                          onClick={() => {
                            setDownloading('download')
                            axios
                              .get<string>(
                                `/api/manage/nremt/image/${nremt.id}`,
                              )
                              .then(pluckData)
                              .then((response) =>
                                axios
                                  .get<Blob>(response, { responseType: 'blob' })
                                  .catch(() => {
                                    // eslint-disable-next-line fp/no-throw, no-throw-literal
                                    throw 'File Not Found'
                                  }),
                              )
                              .then(pluckData)
                              .then((response) =>
                                downloadFile(
                                  response,
                                  `${user.lastName}-nremt-license.${extensions[response.type as ContentType]}`,
                                ),
                              )
                              .catch((err) => {
                                showError(
                                  `Image Download Failed: ${err.response?.data || err}`,
                                )
                              })
                              .finally(() => setDownloading(undefined))
                          }}
                        >
                          <DownloadIcon aria-hidden="true" />
                        </IconButton>
                      </Tooltip>
                      <Dialog
                        open={!!viewBlob}
                        onClose={() => setViewBlob(null)}
                        maxWidth="lg"
                      >
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            position: 'relative',
                            overflow: 'scroll',
                          }}
                        >
                          <IconButton
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                            size="small"
                            aria-label="Close NREMT image preview"
                            onClick={() => setViewBlob(null)}
                          >
                            <CloseIcon />
                          </IconButton>
                          <Typography
                            sx={{
                              textAlign: 'center',
                              fontSize: 20,
                              mb: 0.5,
                            }}
                          >
                            View NREMT
                          </Typography>
                          <Box>
                            {viewBlob && (
                              <img
                                src={URL.createObjectURL(viewBlob)}
                                alt="View NREMT Image"
                              />
                            )}
                          </Box>
                        </Card>
                      </Dialog>
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          </Box>
          {linkedServiceID && userID && (
            <NREMTLicenseForm
              nremtLicense={nremt}
              lsid={linkedServiceID}
              userID={userID}
              open={nremtOpen}
              setOpen={setNREMTOpen}
              onError={showError}
            />
          )}
          <LicensesDataGrid
            licensesRows={licenses}
            selectedUserLicensesCredentialsStatus={
              selectedUserLicensesCredentialsStatus
            }
            total={licenses.length}
            expiredLicenses={expiredLicenses}
            expiringSoonLicenses={expiringSoonLicenses}
            userID={userID!}
            verificationEnabled={licenseVerificationEnabled}
            licenseVerificationStatuses={licenseVerifications}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <CredentialsDataGrid
            credentialsRows={credentials}
            selectedUserLicensesCredentialsStatus={
              selectedUserLicensesCredentialsStatus
            }
            totalCredentials={credentials.length}
            expiredCredentials={expiredCredentials}
            expiringSoonCredentials={expiringSoonCredentials}
            userID={userID!}
            verificationEnabled={verificationEnabled}
            verificationStatuses={credentialVerifications}
            onRefresh={() => {
              if (userID) {
                dispatch(fetchSelectedUserLicensesCredentials(userID))
              }
            }}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <UserActivityTimeline
            userID={userID!}
            linkedServiceID={linkedServiceID!}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <LearningOverviewTab
            selectedUserCertificates={selectedUserCertificates}
            isLoading={selectedUserCertificatesStatus === 'pending'}
            hasError={selectedUserCertificatesStatus === 'rejected'}
            hasLinkedService
            downloadHook={managerDownloadHook}
          />
        </TabPanel>
      </Card>
      {NotificationComponent}
    </Container>
  )
}
